import { useCallback, useState } from "react";
import { todayInTimeZone } from "../../../../common/src";
import { useAction } from "../../hooks/useAction";
import { useAsyncData } from "../../hooks/useAsyncData";
import { useCurrentUser } from "../../session/SessionContext";
import { navigate } from "../../hooks/useRoute";
import { DEFAULT_ROUTE } from "../../routes";
import { trpc } from "../../trpc";
import { Button } from "../shared/Button";
import { Card } from "../shared/Card";
import { Notice } from "../shared/Notice";
import { EmptyState, LoadError, Loading } from "../shared/StatusViews";
import { ReservationList } from "./ReservationList";
import { useLayoutStyles } from "../shared/styles";

const localToday = (): string =>
  todayInTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);

export const ReservationsPage = () => {
  const user = useCurrentUser();
  const layout = useLayoutStyles();
  const isAdmin = user.role === "admin";
  const [showPast, setShowPast] = useState(false);
  const [cancellingId, setCancellingId] = useState<number | undefined>(
    undefined
  );

  const load = useCallback(
    (signal: AbortSignal) =>
      isAdmin
        ? trpc.reservation.upcoming.query(undefined, { signal })
        : trpc.reservation.mine.query(undefined, { signal }),
    [isAdmin]
  );
  const reservations = useAsyncData(load);
  const cancel = useAction(
    useCallback((id: number) => trpc.reservation.cancel.mutate({ id }), [])
  );

  const onCancel = async (id: number) => {
    setCancellingId(id);
    const result = await cancel.run(id);
    setCancellingId(undefined);
    if (result) {
      reservations.reload();
    }
  };

  const today = localToday();
  const all = reservations.data ?? [];
  const upcoming = all
    .filter((r) => r.endDate >= today && r.status === "confirmed")
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
  const history = all.filter(
    (r) => !(r.endDate >= today && r.status === "confirmed")
  );

  const title = isAdmin
    ? "Upcoming reservations (everyone)"
    : "My upcoming reservations";

  return (
    <div className={layout.sections}>
      <Card title={title}>
        {cancel.error && (
          <Notice kind="error" onDismiss={cancel.clearError}>
            {cancel.error}
          </Notice>
        )}
        {reservations.error && (
          <LoadError
            message={reservations.error}
            onRetry={reservations.reload}
          />
        )}
        {reservations.loading && !reservations.data && <Loading />}
        {reservations.data && upcoming.length === 0 && (
          <EmptyState
            title="No upcoming reservations"
            action={
              <Button onClick={() => navigate(DEFAULT_ROUTE)}>
                Find a boat
              </Button>
            }
          >
            {isAdmin
              ? "Nobody has booked the boat yet."
              : "Find a boat and pick your dates to make a reservation."}
          </EmptyState>
        )}
        {upcoming.length > 0 && (
          <ReservationList
            reservations={upcoming}
            today={today}
            showPerson={isAdmin}
            cancellingId={cancellingId}
            onCancel={(id) => void onCancel(id)}
          />
        )}
      </Card>
      {history.length > 0 && (
        <Card
          title={isAdmin ? "Cancelled" : "Past and cancelled"}
          actions={
            <Button
              variant="ghost"
              small
              onClick={() => setShowPast((v) => !v)}
            >
              {showPast ? "Hide" : `Show (${history.length})`}
            </Button>
          }
        >
          {showPast && (
            <ReservationList
              reservations={history}
              today={today}
              showPerson={isAdmin}
              cancellingId={undefined}
              onCancel={() => undefined}
            />
          )}
        </Card>
      )}
    </div>
  );
};
